/**
 * <mb-form>: envia formulários nativos da Shopify (newsletter/contato) por fetch,
 * mantendo o comportamento do tema anterior: botão "Enviando", mensagem de sucesso/erro inline,
 * reset e retorno ao estado inicial após 3,5 s. Honeypot: campo "contact[extra]" preenchido = não envia.
 *
 * hCaptcha: a Shopify recusa o POST em /contact sem o token "h-captcha-response". O script dela liga o captcha
 * ao formulário no primeiro foco (marca data-hcaptcha-bound) e troca form.submit() por uma versão que gera o
 * token e depois chama o form.submit() anterior. Aqui form.submit() é o envio por fetch, então o token vai junto.
 * Se a Shopify desconfiar do envio mesmo com o token, ela responde com a página /challenge ("confirme que você
 * não é um robô", com o envio guardado): a cliente é levada para lá e, ao confirmar, volta para a página com
 * ?contact_posted=true (ou customer_posted); aí o formulário que ela enviou mostra a mensagem de sucesso.
 */
const { emit } = window.MB;

/** Formulário que foi para a verificação da Shopify (sessionStorage: sobrevive à ida e volta na mesma aba). */
const CHALLENGE_KEY = 'mb:form:challenge';

class MbForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.button = this.form.querySelector('[type="submit"]');
    this.messages = {
      success: this.querySelector('[data-msg-success]'),
      error: this.querySelector('[data-msg-error]'),
    };
    this.texts = {
      default: this.button ? this.button.innerHTML : '',
      sending: this.dataset.sending || '',
      sent: this.dataset.sent || '',
      error: this.dataset.error || '',
    };
    this.form.submit = () => this.send();
    this.form.addEventListener('submit', (event) => this.submit(event));
    // Com a página carregada: o product-form.js e o kit-form.js escondem o avise-me ao iniciar, e até lá as
    // fotos ainda mudam a altura da página (a rolagem passaria do formulário).
    if (document.readyState === 'complete') this.showChallengeResult();
    else window.addEventListener('load', () => this.showChallengeResult(), { once: true });
  }

  /** Volta da verificação da Shopify: mostra o sucesso no formulário que foi enviado. */
  showChallengeResult() {
    if (!/[?&](contact|customer)_posted=true/.test(window.location.search)) return;
    try {
      if (!this.form.id || sessionStorage.getItem(CHALLENGE_KEY) !== this.form.id) return;
      sessionStorage.removeItem(CHALLENGE_KEY);
    } catch (error) {
      return;
    }
    // O avise-me fica oculto quando a variante aberta está disponível.
    if (this.dataset.type === 'notify') this.form.classList.add('-active');
    this.setState('sent');
    this.scrollIntoView({ block: 'center' });
    setTimeout(() => this.setState('default'), 8000);
  }

  submit(event) {
    event.preventDefault();
    const honeypot = this.form.querySelector('[data-honeypot]');
    if ((honeypot && honeypot.value) || !this.form.reportValidity()) {
      // Não deixa o captcha da Shopify gerar o token nem enviar.
      event.stopImmediatePropagation();
      return;
    }

    this.setState('sending');
    // Se o captcha não terminar (desafio fechado pela cliente), o botão não fica preso em "Enviando".
    clearTimeout(this.stuckTimer);
    this.stuckTimer = setTimeout(() => this.setState('default'), 20000);

    // Captcha já ligado: o listener dele, registrado depois deste, gera o token e chama form.submit().
    if (this.form.dataset.hcaptchaBound) return;
    event.stopImmediatePropagation();
    this.captchaReady().then(() => this.form.submit());
  }

  /** Liga o captcha da Shopify ao formulário, se a loja usa, e espera ficar pronto (desiste em 8 s). */
  captchaReady() {
    const captcha = window.Shopify && window.Shopify.captcha;
    if (!captcha || this.form.dataset.hcaptchaBound) return Promise.resolve();
    return new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        if (this.form.dataset.hcaptchaBound) done();
      });
      const timer = setTimeout(() => done(), 8000);
      const done = () => {
        observer.disconnect();
        clearTimeout(timer);
        resolve();
      };
      observer.observe(this.form, { attributes: true, attributeFilter: ['data-hcaptcha-bound'] });
      // data-cptcha = o script da Shopify já começou a ligar (no foco); aí é só esperar.
      if (!this.form.dataset.cptcha) captcha.protect(this.form);
    });
  }

  async send() {
    clearTimeout(this.stuckTimer);
    this.setState('sending');
    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'text/html' },
        redirect: 'follow',
      });
      if (new URL(response.url).pathname.startsWith('/challenge')) {
        try {
          sessionStorage.setItem(CHALLENGE_KEY, this.form.id);
        } catch (error) {
          // Sem sessionStorage a verificação funciona igual; só não aparece a mensagem na volta.
        }
        window.location.href = response.url;
        return;
      }
      // Só é sucesso quando a Shopify redireciona (302) para a página com ?customer_posted=true ou
      // ?contact_posted=true; captcha recusado ou campo com erro não chegam nessa URL.
      if (response.ok && /posted=true/i.test(response.url)) {
        this.setState('sent');
        emit('mb:form:sent', { form: this.form, type: this.dataset.type });
        this.form.reset();
      } else {
        this.setState('error');
      }
    } catch (error) {
      this.setState('error');
    }
    setTimeout(() => this.setState('default'), 3500);
  }

  setState(state) {
    this.form.classList.remove('sending', 'sent', 'error');
    if (state !== 'default') this.form.classList.add(state);
    if (this.button) {
      this.button.innerHTML = this.texts[state] || this.texts.default;
      this.button.classList.toggle('-sending', state === 'sending');
    }
    Object.entries(this.messages).forEach(([key, el]) => {
      if (!el) return;
      el.classList.toggle('-visible', (state === 'sent' && key === 'success') || (state === 'error' && key === 'error'));
    });
  }
}

if (!customElements.get('mb-form')) customElements.define('mb-form', MbForm);
